// Reads the user's spendable credit balance + monthly free quota and the ledger.
// Balances are mutated only server-side (SECURITY DEFINER functions); this is read-only.

import { supabase } from './supabaseClient';

export const FREE_MONTHLY_CREDITS = 100;

export interface CreditSummary {
  balance: number;          // paid credits
  freeRemaining: number;    // remaining free allowance this month
  total: number;            // balance + freeRemaining (spendable now)
}

export interface CreditTxn {
  id: string;
  amount: number;
  reason: string | null;
  action: string | null;
  created_at: string;
}

const monthStart = (): string => {
  const n = new Date();
  return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), 1)).toISOString().slice(0, 10);
};

export const fetchCredits = async (): Promise<CreditSummary> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { balance: 0, freeRemaining: 0, total: 0 };

  const { data } = await supabase
    .from('users')
    .select('credit_balance, free_quota_used, free_quota_month')
    .eq('id', user.id)
    .maybeSingle();

  const balance = data?.credit_balance ?? 0;
  // Free allowance resets monthly; if the stored month is older, the full quota is available.
  const used = data?.free_quota_month && data.free_quota_month >= monthStart()
    ? (data.free_quota_used ?? 0)
    : 0;
  const freeRemaining = Math.max(0, FREE_MONTHLY_CREDITS - used);
  return { balance, freeRemaining, total: balance + freeRemaining };
};

export const fetchCreditLedger = async (limit = 12): Promise<CreditTxn[]> => {
  const { data, error } = await supabase
    .from('credit_ledger')
    .select('id, amount, reason, action, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.error('ledger read failed', error); return []; }
  return (data as CreditTxn[]) || [];
};
